import { Node } from '@xyflow/react'

export const getManagerLabel = (index: number, structureType: string): string => {
  return `Manager ${index + 1}`
}

export const generateNodesBasedOnStructure = (requirements: any): Node[] => {
  const newNodes: Node[] = []
  const levelHeight = 150
  const nodeWidth = requirements.organizational_structure === 'flat' ? 200 : 300
  const centerX = window.innerWidth / 2
  const maxNodesInLevel = Math.max(
    requirements.directors_count,
    requirements.managers_count,
    requirements.team_members_count
  )
  const canvasWidth = Math.max(window.innerWidth, maxNodesInLevel * nodeWidth + 200)
  const getTotalWidth = (nodesCount: number) => nodesCount * nodeWidth
  const getStartX = (nodesCount: number) => {
    const totalWidth = getTotalWidth(nodesCount)
    return centerX - (totalWidth / 2)
  }
  
  const directorsStartX = getStartX(requirements.directors_count)
  
  for (let i = 0; i < requirements.directors_count; i++) {
    newNodes.push({
      id: `director-${i}`,
      type: 'roleNode',
      data: { label: `Director ${i + 1}`, type: 'director' },
      position: { x: directorsStartX + (i * nodeWidth), y: 0 },
      draggable: true,
    })
  }

  const alignTeamMembersUnderManagers = () => {
    if (requirements.managers_count === 0) {
      const maxTeamMembersPerRow = 4
      const teamMemberHorizontalGap = 20
      const teamMemberVerticalGap = 100
      const startY = levelHeight + 50
      
      const totalRowWidth = (nodeWidth * Math.min(maxTeamMembersPerRow, requirements.team_members_count)) + 
                          (teamMemberHorizontalGap * (Math.min(maxTeamMembersPerRow, requirements.team_members_count) - 1))
      const startX = centerX - (totalRowWidth / 2)

      for (let i = 0; i < requirements.team_members_count; i++) {
        const row = Math.floor(i / maxTeamMembersPerRow)
        const col = i % maxTeamMembersPerRow
        
        newNodes.push({
          id: `team-member-${i}`,
          type: 'roleNode',
          data: { label: `Team Member ${i + 1}`, type: 'team-member' },
          position: { 
            x: startX + (col * (nodeWidth + teamMemberHorizontalGap)),
            y: startY + (row * teamMemberVerticalGap)
          },
          draggable: true,
        })
      }
      return
    }

    const baseTeamMembersPerManager = Math.floor(requirements.team_members_count / requirements.managers_count)
    const extraTeamMembers = requirements.team_members_count % requirements.managers_count
    let currentTeamMember = 0

    const managersStartX = getStartX(requirements.managers_count)
    const managerNodes = []

    for (let i = 0; i < requirements.managers_count; i++) {
      const managerLabel = getManagerLabel(i, requirements.organizational_structure)
      const managerX = managersStartX + (i * nodeWidth)

      const managerNode = {
        id: `manager-${i}`,
        type: 'roleNode',
        data: { label: managerLabel, type: 'manager' },
        position: { 
          x: managerX,
          y: levelHeight,
          offsetX: managerX + (nodeWidth / 2)
        },
        draggable: true,
      }
      managerNodes.push(managerNode)
      newNodes.push(managerNode)
    }

    for (let managerIndex = 0; managerIndex < requirements.managers_count; managerIndex++) {
      const extraForThisManager = managerIndex < extraTeamMembers ? 1 : 0
      const teamMembersForThisManager = baseTeamMembersPerManager + extraForThisManager
      
      const managerCenterX = managerNodes[managerIndex].position.offsetX
      
      const teamMemberVerticalGap = 100
      const startY = levelHeight + 150
      
      for (let i = 0; i < teamMembersForThisManager; i++) {
        newNodes.push({
          id: `team-member-${currentTeamMember}`,
          type: 'roleNode',
          data: { label: `Team Member ${currentTeamMember + 1}`, type: 'team-member' },
          position: { 
            x: managerCenterX,
            y: startY + (i * teamMemberVerticalGap)
          },
          draggable: true,
        })
        currentTeamMember++
      }
    }
  }

  switch (requirements.organizational_structure) {
    case 'flat': {
      const totalLowerNodes = requirements.managers_count + requirements.team_members_count
      const lowerNodesStartX = getStartX(totalLowerNodes)
      let currentX = lowerNodesStartX
      const horizontalGap = requirements.organizational_structure === 'flat' ? 20 : 50

      for (let i = 0; i < requirements.managers_count; i++) {
        const managerLabel = getManagerLabel(i, requirements.organizational_structure)
        newNodes.push({
          id: `manager-${i}`,
          type: 'roleNode',
          data: { label: managerLabel, type: 'manager' },
          position: { x: currentX, y: levelHeight },
          draggable: true,
        })
        currentX += nodeWidth + horizontalGap
      }

      for (let i = 0; i < requirements.team_members_count; i++) {
        newNodes.push({
          id: `team-member-${i}`,
          type: 'roleNode',
          data: { label: `Team Member ${i + 1}`, type: 'team-member' },
          position: { x: currentX, y: levelHeight },
          draggable: true,
        })
        currentX += nodeWidth + horizontalGap
      }
      break
    }
    case 'hierarchical':
    case 'functional':
    case 'divisional':
    case 'matrix':
    case 'team':
    case 'line':
    case 'process': {
      alignTeamMembersUnderManagers()
      break
    }
    case 'network': {
      const centerX = window.innerWidth / 2
      const centerY = window.innerHeight / 2
      const directorRadius = 0
      const managerRadius = 300
      const teamMemberRadius = 600
      
      for (let i = 0; i < requirements.directors_count; i++) {
        newNodes.push({
          id: `director-${i}`,
          type: 'roleNode',
          data: { label: `Director ${i + 1}`, type: 'director' },
          position: {
            x: centerX - nodeWidth / 2,
            y: centerY - levelHeight / 2
          },
          draggable: true,
        })
      }
      
      const managerAngleStep = (2 * Math.PI) / requirements.managers_count
      for (let i = 0; i < requirements.managers_count; i++) {
        const angle = i * managerAngleStep
        const managerLabel = getManagerLabel(i, requirements.organizational_structure)
        newNodes.push({
          id: `manager-${i}`,
          type: 'roleNode',
          data: { label: managerLabel, type: 'manager' },
          position: {
            x: centerX + Math.cos(angle) * managerRadius - nodeWidth / 2,
            y: centerY + Math.sin(angle) * managerRadius - levelHeight / 2
          },
          draggable: true,
        })
      }
      
      const teamMemberAngleStep = (2 * Math.PI) / requirements.team_members_count
      for (let i = 0; i < requirements.team_members_count; i++) {
        const angle = i * teamMemberAngleStep
        newNodes.push({
          id: `team-member-${i}`,
          type: 'roleNode',
          data: { label: `Team Member ${i + 1}`, type: 'team-member' },
          position: {
            x: centerX + Math.cos(angle) * teamMemberRadius - nodeWidth / 2,
            y: centerY + Math.sin(angle) * teamMemberRadius - levelHeight / 2
          },
          draggable: true,
        })
      }
      break
    }
    case 'circular': {
      const centerX = window.innerWidth / 2
      const centerY = window.innerHeight / 2
      const teamMemberRadius = 600
      const managerRadius = 300
      const teamMemberAngleStep = (2 * Math.PI) / requirements.team_members_count
      const managerAngleStep = (2 * Math.PI) / requirements.managers_count
      
      for (let i = 0; i < requirements.directors_count; i++) {
        newNodes.push({
          id: `director-${i}`,
          type: 'roleNode',
          data: { label: `Director ${i + 1}`, type: 'director' },
          position: {
            x: centerX - nodeWidth / 2,
            y: centerY - levelHeight / 2
          },
          draggable: true,
        })
      }
      
      for (let i = 0; i < requirements.managers_count; i++) {
        const angle = i * managerAngleStep
        const managerLabel = getManagerLabel(i, requirements.organizational_structure)
        newNodes.push({
          id: `manager-${i}`,
          type: 'roleNode',
          data: { label: managerLabel, type: 'manager' },
          position: {
            x: centerX + Math.cos(angle) * managerRadius - nodeWidth / 2,
            y: centerY + Math.sin(angle) * managerRadius - levelHeight / 2
          },
          draggable: true,
        })
      }
      
      for (let i = 0; i < requirements.team_members_count; i++) {
        const angle = i * teamMemberAngleStep
        newNodes.push({
          id: `team-member-${i}`,
          type: 'roleNode',
          data: { label: `Team Member ${i + 1}`, type: 'team-member' },
          position: {
            x: centerX + Math.cos(angle) * teamMemberRadius - nodeWidth / 2,
            y: centerY + Math.sin(angle) * teamMemberRadius - levelHeight / 2
          },
          draggable: true,
        })
      }
      break
    }
  }

  return newNodes
}